import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Search, Loader2, X, Plus, Trash2, Pencil, ChevronRight, ChevronDown,
  UsersRound, AlertTriangle, RefreshCcw, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { useEnvironment } from '@/contexts/EnvironmentContext';
import {
  listEnterprises, createEnterprise, updateEnterprise, deleteEnterprise, purgeEnterprise,
  listTeams, createTeam, updateTeam, deleteTeam, listPlans,
  Enterprise, Team, PlanTier,
} from '@/services/organizationApi';

const getErrorMessage = (err: unknown): string => {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: string } } }).response;
    return response?.data?.error || '';
  }
  if (err && typeof err === 'object' && 'message' in err) {
    return (err as { message: string }).message;
  }
  return '';
};

type DialogMode = 'create' | 'edit';
type DialogEntity = 'enterprise' | 'team';

interface DialogState {
  open: boolean;
  mode: DialogMode;
  entity: DialogEntity;
  name: string;
  description: string;
  enterpriseId: string;
  parentId: string | null;
  editId: string;
  planSlug: string;
}

const emptyDialog: DialogState = {
  open: false, mode: 'create', entity: 'enterprise',
  name: '', description: '', enterpriseId: '', parentId: null, editId: '', planSlug: '',
};

interface TeamNode {
  team: Team;
  children: TeamNode[];
  expanded: boolean;
}

interface EnterpriseNode {
  enterprise: Enterprise;
  teams: TeamNode[];
  expanded: boolean;
  loading: boolean;
}

/** Build a tree from a flat list of teams by parent_id */
function buildTeamTree(teams: Team[]): TeamNode[] {
  const map = new Map<string, TeamNode>();
  const roots: TeamNode[] = [];

  for (const team of teams) {
    map.set(team.id, { team, children: [], expanded: false });
  }

  for (const team of teams) {
    const node = map.get(team.id)!;
    if (team.parent_id && map.has(team.parent_id)) {
      map.get(team.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

function TeamTreeRow({
  node, depth, onAdd, onEdit, onDelete,
}: {
  node: TeamNode;
  depth: number;
  onAdd: (enterpriseId: string, parentId: string) => void;
  onEdit: (id: string, name: string, description: string) => void;
  onDelete: (id: string, name: string) => void;
}) {
  const [expanded, setExpanded] = useState(node.team.is_root); // root teams start expanded

  const hasChildren = node.children.length > 0;

  return (
    <>
      <div
        className="group flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
        style={{ paddingLeft: `${12 + depth * 24}px` }}
      >
        {hasChildren ? (
          <button onClick={() => setExpanded(!expanded)} className="p-0.5 hover:bg-gray-200 rounded">
            {expanded
              ? <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
              : <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
            }
          </button>
        ) : (
          <span className="w-4.5" />
        )}
        <UsersRound className={`w-3.5 h-3.5 ${node.team.is_root ? 'text-emerald-500' : 'text-orange-500'}`} />
        <span className={`text-sm flex-1 ${node.team.is_root ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
          {node.team.name}
        </span>
        {node.team.description && (
          <span className="text-xs text-gray-400 mr-2 truncate max-w-[200px]">{node.team.description}</span>
        )}
        <div className="hidden group-hover:flex items-center gap-1">
          <button
            onClick={() => onAdd(node.team.enterprise_id, node.team.id)}
            className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
            title="Add child team"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          {!node.team.is_root && (
            <>
              <button
                onClick={() => onEdit(node.team.id, node.team.name, node.team.description || '')}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                title="Edit"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => onDelete(node.team.id, node.team.name)}
                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                title="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
      {expanded && node.children.map(child => (
        <TeamTreeRow
          key={child.team.id}
          node={child}
          depth={depth + 1}
          onAdd={onAdd}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </>
  );
}

export default function EnterprisesPage() {
  const navigate = useNavigate();
  const { current } = useEnvironment();

  const [enterprises, setEnterprises] = useState<EnterpriseNode[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(emptyDialog);
  const [deleteDialog, setDeleteDialog] = useState<{ entity: DialogEntity; id: string; name: string } | null>(null);
  const [plans, setPlans] = useState<PlanTier[]>([]);

  useEffect(() => {
    listPlans().then(setPlans).catch(() => { /* noop */ });
  }, []);

  const loadEnterprises = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listEnterprises();
      setEnterprises(data.map(e => ({ enterprise: e, teams: [], expanded: false, loading: false })));
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to load enterprises');
      setEnterprises([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEnterprises(); }, [loadEnterprises]);

  const toggleEnterprise = async (idx: number) => {
    const node = enterprises[idx];
    if (node.expanded) {
      setEnterprises(prev => prev.map((e, i) => i === idx ? { ...e, expanded: false } : e));
      return;
    }
    // Load teams
    setEnterprises(prev => prev.map((e, i) => i === idx ? { ...e, expanded: true, loading: true } : e));
    try {
      const teams = await listTeams(node.enterprise.id);
      const tree = buildTeamTree(teams);
      setEnterprises(prev => prev.map((e, i) =>
        i === idx ? { ...e, loading: false, teams: tree } : e
      ));
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to load teams');
      setEnterprises(prev => prev.map((e, i) => i === idx ? { ...e, loading: false } : e));
    }
  };

  const openCreateTeamDialog = (enterpriseId: string, parentId: string | null) => {
    setDialog({ open: true, mode: 'create', entity: 'team', name: '', description: '', enterpriseId, parentId, editId: '', planSlug: '' });
  };

  const openEditTeamDialog = (id: string, name: string, description: string) => {
    setDialog({ open: true, mode: 'edit', entity: 'team', name, description, enterpriseId: '', parentId: null, editId: id, planSlug: '' });
  };

  const handleDialogSubmit = async () => {
    if (!dialog.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      setActionLoading(true);
      if (dialog.mode === 'create') {
        if (dialog.entity === 'enterprise') {
          await createEnterprise({ name: dialog.name, plan_slug: dialog.planSlug || undefined });
          toast.success('Enterprise created');
        } else {
          await createTeam({
            enterprise_id: dialog.enterpriseId,
            parent_id: dialog.parentId,
            name: dialog.name,
            description: dialog.description || undefined,
          });
          toast.success('Team created');
        }
      } else {
        if (dialog.entity === 'enterprise') {
          await updateEnterprise(dialog.editId, { name: dialog.name, plan_slug: dialog.planSlug || undefined });
          toast.success('Enterprise updated');
        } else {
          await updateTeam(dialog.editId, { name: dialog.name, description: dialog.description || undefined });
          toast.success('Team updated');
        }
      }
      setDialog(emptyDialog);
      await loadEnterprises();
    } catch (err) {
      toast.error(getErrorMessage(err) || `Failed to ${dialog.mode} ${dialog.entity}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      setActionLoading(true);
      if (deleteDialog.entity === 'enterprise') {
        await deleteEnterprise(deleteDialog.id);
        toast.success('Enterprise deleted');
      } else {
        await deleteTeam(deleteDialog.id);
        toast.success('Team deleted');
      }
      setDeleteDialog(null);
      await loadEnterprises();
    } catch (err) {
      toast.error(getErrorMessage(err) || `Failed to delete ${deleteDialog.entity}`);
    } finally {
      setActionLoading(false);
    }
  };

  const [purgeDialog, setPurgeDialog] = useState<{ id: string; name: string; slug: string; confirmText: string } | null>(null);

  const handlePurge = async () => {
    // Confirmation par slug (unique, immutable, suffixe random) plutot que par
    // nom : depuis migration 078, le name n'est plus UNIQUE et deux enterprises
    // peuvent partager le meme display name. Demander le slug elimine le risque
    // de purger la mauvaise enterprise homonyme par erreur.
    if (!purgeDialog || purgeDialog.confirmText !== purgeDialog.slug) return;
    try {
      setActionLoading(true);
      const result = await purgeEnterprise(purgeDialog.id);
      toast.success(result.message);
      setPurgeDialog(null);
      await loadEnterprises();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to purge enterprise');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredEnterprises = enterprises.filter(e =>
    !searchQuery || e.enterprise.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const entityLabel = (entity: DialogEntity) =>
    entity === 'enterprise' ? 'Enterprise' : 'Team';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6 text-indigo-500" />
          <h2 className="text-xl font-semibold text-gray-800">Enterprises</h2>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            ({ blue: 'bg-blue-100 text-blue-700', amber: 'bg-amber-100 text-amber-700', red: 'bg-red-100 text-red-700' })[current.color]
          }`}>
            {current.label}
          </span>
        </div>
        <button
          onClick={() => setDialog({ ...emptyDialog, open: true, mode: 'create', entity: 'enterprise' })}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          Create Enterprise
        </button>
      </div>

      {current.color === 'red' && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          You are managing the <strong>production</strong> environment.
        </div>
      )}

      {/* Search & Refresh */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search enterprises..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <button
          onClick={loadEnterprises}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          title="Refresh"
        >
          <RefreshCcw className="w-4 h-4" />
        </button>
      </div>

      {/* Tree */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : filteredEnterprises.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-12">No enterprises found</p>
        ) : (
          <div className="space-y-1">
            {filteredEnterprises.map((entNode) => {
              const realIdx = enterprises.indexOf(entNode);
              return (
                <div key={entNode.enterprise.id}>
                  {/* Enterprise row */}
                  <div className="group flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                    <button onClick={() => toggleEnterprise(realIdx)} className="p-0.5 hover:bg-gray-200 rounded">
                      {entNode.expanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                    </button>
                    <Building2 className="w-4 h-4 text-indigo-500" />
                    <span className="font-medium text-sm text-gray-900 flex-1">{entNode.enterprise.name}</span>
                    {entNode.enterprise.plan_name && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        entNode.enterprise.plan_slug === 'beta' ? 'bg-purple-100 text-purple-700' :
                        entNode.enterprise.plan_slug === 'hoshin' ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {entNode.enterprise.plan_name}
                      </span>
                    )}
                    <span className="text-xs font-mono text-gray-500 mr-2 px-1.5 py-0.5 bg-gray-100 rounded" title="Slug — unique identifier">
                      {entNode.enterprise.slug}
                    </span>
                    <div className="hidden group-hover:flex items-center gap-1">
                      <button
                        onClick={() => openCreateTeamDialog(entNode.enterprise.id, null)}
                        className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                        title="Add Team"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setDialog({ open: true, mode: 'edit', entity: 'enterprise', name: entNode.enterprise.name, description: '', enterpriseId: '', parentId: null, editId: entNode.enterprise.id, planSlug: entNode.enterprise.plan_slug || '' })}
                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => navigate(`/enterprises/${entNode.enterprise.id}/contract`)}
                        className="p-1 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                        title="Gérer le contrat (facturation hors Stripe)"
                      >
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setDeleteDialog({ entity: 'enterprise', id: entNode.enterprise.id, name: entNode.enterprise.name })}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete (soft)"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setPurgeDialog({ id: entNode.enterprise.id, name: entNode.enterprise.name, slug: entNode.enterprise.slug, confirmText: '' })}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Purge (permanent)"
                      >
                        <AlertTriangle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Teams tree */}
                  {entNode.expanded && (
                    <div className="ml-4">
                      {entNode.loading ? (
                        <div className="flex items-center gap-2 py-2 px-3 text-sm text-gray-400">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading teams...
                        </div>
                      ) : entNode.teams.length === 0 ? (
                        <p className="py-2 px-3 text-xs text-gray-400">No teams yet</p>
                      ) : (
                        entNode.teams.map(teamNode => (
                          <TeamTreeRow
                            key={teamNode.team.id}
                            node={teamNode}
                            depth={0}
                            onAdd={openCreateTeamDialog}
                            onEdit={openEditTeamDialog}
                            onDelete={(id, name) => setDeleteDialog({ entity: 'team', id, name })}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      {dialog.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {dialog.mode === 'create' ? 'Create' : 'Edit'} {entityLabel(dialog.entity)}
              </h3>
              <button onClick={() => setDialog(emptyDialog)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={dialog.name}
                  onChange={(e) => setDialog({ ...dialog, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={`${entityLabel(dialog.entity)} name`}
                  autoFocus
                />
              </div>

              {dialog.entity === 'enterprise' && plans.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                  <select
                    value={dialog.planSlug}
                    onChange={(e) => setDialog({ ...dialog, planSlug: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">— No plan —</option>
                    {plans.map(plan => (
                      <option key={plan.slug} value={plan.slug}>
                        {plan.name} {plan.price_monthly_cents === 0 ? '(Free)' : `(${plan.price_monthly_cents / 100}€/${plan.pricing_model === 'flat' ? 'mo' : 'team/mo'})`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {dialog.entity === 'team' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={dialog.description}
                    onChange={(e) => setDialog({ ...dialog, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Optional description"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setDialog(emptyDialog)}
                disabled={actionLoading}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDialogSubmit}
                disabled={actionLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {dialog.mode === 'create' ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-2">Delete {entityLabel(deleteDialog.entity)}</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{deleteDialog.name}</strong>?
              {deleteDialog.entity === 'enterprise' && ' All teams within this enterprise will also be deactivated.'}
              {deleteDialog.entity === 'team' && ' Child teams will also be deactivated.'}
              {' '}This is a soft-delete and can be reversed.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteDialog(null)}
                disabled={actionLoading}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purge Confirmation Dialog */}
      {purgeDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <h3 className="text-lg font-semibold text-red-600">Permanent deletion</h3>
            </div>
            <p className="text-gray-600 mb-2">
              This will <strong>permanently delete</strong> enterprise <strong>{purgeDialog.name}</strong>{' '}
              <span className="font-mono text-xs text-gray-500">({purgeDialog.slug})</span> and ALL related data:
            </p>
            <ul className="text-sm text-gray-500 mb-4 list-disc ml-5 space-y-1">
              <li>All users and their accounts</li>
              <li>All teams and hierarchy</li>
              <li>Subscriptions and billing events</li>
              <li>Configurations (GitHub, Jira, LLM, etc.)</li>
              <li>Audit logs and sessions</li>
            </ul>
            <p className="text-sm text-gray-600 mb-3">
              Type the slug <strong className="font-mono">{purgeDialog.slug}</strong> to confirm
              <span className="block text-xs text-gray-500 mt-1">(le slug est unique, contrairement au nom — il garantit que vous ciblez la bonne entreprise)</span>
            </p>
            <input
              type="text"
              value={purgeDialog.confirmText}
              onChange={(e) => setPurgeDialog({ ...purgeDialog, confirmText: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4 font-mono focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder={purgeDialog.slug}
              autoComplete="off"
              spellCheck={false}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPurgeDialog(null)}
                disabled={actionLoading}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePurge}
                disabled={actionLoading || purgeDialog.confirmText !== purgeDialog.slug}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:bg-red-300 flex items-center gap-2"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Purge permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
